import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, ClickAwayListener, Skeleton, Typography } from '@mui/material'
import { AgGridReact } from 'ag-grid-react'
import { BodyScrollEvent, ColDef, GridApi, GridReadyEvent } from 'ag-grid-community'
import { FilterChangedEvent } from 'ag-grid-enterprise'
import 'ui/components/records-grid/registerAgGrid'

import {
  ASSET_SET_FILTER,
  AssetColumn,
  AssetColumnKind,
  AssetFilter,
  AssetInventoryType,
  AssetRow,
  ColumnValue,
  DevicesColumn,
  LastSeenWindow
} from 'models'
import { AssetInventoryServiceV4 } from 'services'
import useDebounce from 'hooks/useDebounce'
import SearchInput from 'ui/components/form-components/SearchInput'
import { useRecordsColumns } from 'ui/components/records-grid/useRecordsColumns'
import { positionRecordsGridPopup } from 'ui/components/records-grid/gridPopup'
import {
  buildQueryFilter,
  buildSort,
  describeBooleanFilter,
  describeDateFilter,
  describeNumberFilter,
  searchColumnsFor
} from '../utils/assetQuery'
import { primeAssetLookup } from '../utils/assetLookup'
import { formatDataAsOf } from '../utils/assetRows'
import { PREDICATES, SOURCE_COLUMN } from '../assetInventory.columns'
import { productTitle } from '../utils/assetSources'
import { AssetColumnsStatus } from '../hooks/useAssetColumns'
import { SHOW_ASSET_SEARCH } from '../assetInventory.constants'

import ControlButton from 'ui/pages/cpi-details/sections/devices/components/ControlButton'
import FilterChipContainer from 'ui/pages/cpi-details/sections/devices/components/FilterChipContainer'
import { BooleanFilter } from 'ui/pages/cpi-details/sections/devices/components/BooleanFilter'
import { DTPicker } from 'ui/pages/cpi-details/sections/devices/DTPicker'
import FilterLoadingOverlay from 'ui/pages/cpi-details/sections/devices/components/FilterLoadingOverlay'
import NoResultsFound from 'ui/pages/cpi-details/sections/devices/components/NoResultsFound'
import ErrorOverlay from 'ui/pages/cpi-details/sections/devices/components/ErrorOverlay'
import LoadingRow from 'ui/pages/cpi-details/sections/devices/components/LoadingRow'
import { LibraryDetailsHoverTooltip } from 'ui/components/tooltip/LibraryDetailsTooltip'
import { AssetColumnPicker, AssetsGridBody, AssetsGridWrapper, RowCapNotice } from './assetsGrid.styles'
import { AssetSetFilter, describeAssetSetFilter } from './AssetSetFilter'
import SourceCell from './SourceCell'
import AssetCell from './AssetCell'

const PAGE_SIZE = 1000
/* The records endpoint refuses later pages; past this the list is narrowed with filters, not scrolled. */
const MAX_PAGE = 100
const MAX_ROWS = PAGE_SIZE * MAX_PAGE
const ROW_CAP: AssetRow = { id: 'row-cap', sources: [], memberCount: 0, values: {}, badges: {} }
const SEARCH_DEBOUNCE_MS = 350
const NO_DATA_SOURCES: never[] = []

/* Number columns filter by range only: the endpoint takes gte_/lte_/between_ and nothing else. */
const NUMBER_FILTER_PARAMS = {
  filterOptions: ['inRange', 'greaterThanOrEqual', 'lessThanOrEqual'],
  maxNumConditions: 1,
  buttons: ['reset', 'apply'],
  closeOnApply: true
}

interface Props {
  title: string
  icon: JSX.Element
  assetType: AssetInventoryType
  assetColumns: AssetColumn[]
  columnsStatus: AssetColumnsStatus
  lastSeen: LastSeenWindow
  externalFilters: AssetFilter[]
  onClearFilter: (id: string) => void
  onRowClick?: (row: AssetRow) => void
  /* Fired as the user scrolls the rows down, so the page can make room for them. */
  onScrollDown?: () => void
}

const HeaderSkeleton = () => (
  <Skeleton
    variant="rectangular"
    width={50}
    height={16}
    animation="wave"
    sx={{ display: 'inline-block', borderRadius: '4px', verticalAlign: 'middle' }}
  />
)

const RowCap = ({ total, noun }: { total: number; noun: string }) => (
  <RowCapNotice>
    Showing the first {MAX_ROWS.toLocaleString()} of {total.toLocaleString()} {noun}. Add filters to narrow the list.
  </RowCapNotice>
)

const AssetsGrid: FC<Props> = ({
  title,
  icon,
  assetType,
  assetColumns,
  columnsStatus,
  lastSeen,
  externalFilters,
  onClearFilter,
  onRowClick,
  onScrollDown
}) => {
  const gridRef = useRef<AgGridReact>(null)
  const [columnValues, setColumnValues] = useState<ColumnValue[]>([])
  const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(null)
  const [columnsPopperOpen, setColumnsPopperOpen] = useState(false)
  const [activeFilters, setActiveFilters] = useState<{ filterName: string; filterValue: string[] }[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null)
  const [available, setAvailable] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const [isFilterLoading, setIsFilterLoading] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch: string = useDebounce(search, SEARCH_DEBOUNCE_MS)
  const [gridApi, setGridApi] = useState<GridApi | null>(null)

  const columnFor = useCallback(
    (name?: string | null) => assetColumns.find((column) => column.name === name),
    [assetColumns]
  )

  /**
   * The shared column hook only needs to know whether a column has anything to offer, so it gets
   * the first page; the set filter subscribes separately and drains the rest as it opens.
   */
  const getLookup = useCallback(
    (columnId: string) => {
      const column = columnFor(columnId)

      /* Number columns are range-only and answer /lookup with a 400. */
      return column && column.kind !== AssetColumnKind.Number
        ? primeAssetLookup(assetType, columnId)
        : Promise.resolve([])
    },
    [assetType, columnFor]
  )

  /* Source leads, and is listed in the column picker like any other column. */
  const gridColumns = useMemo<DevicesColumn[]>(
    () =>
      [SOURCE_COLUMN, ...assetColumns].map((column) => ({
        name: column.name,
        displayName: column.displayName,
        type: column.kind,
        active: column.activeByDefault,
        filterPredicates: PREDICATES[column.kind]
      })),
    [assetColumns]
  )

  const sourceColDef = useMemo<ColDef>(
    () => ({
      colId: SOURCE_COLUMN.name,
      field: SOURCE_COLUMN.name,
      headerName: SOURCE_COLUMN.displayName,
      width: 150,
      sortable: false,
      filter: 'assetSetFilter',
      filterParams: { assetType, columnName: SOURCE_COLUMN.name, enumerate: true, formatValue: productTitle },
      suppressHeaderMenuButton: true,
      cellRenderer: (params: { data?: AssetRow }) => (params.data ? <SourceCell sources={params.data.sources} /> : null)
    }),
    [assetType]
  )

  /* The shared generator builds op-view colDefs; asset rows carry their own cells and sorting. */
  const decorateColDef = useCallback(
    (colDef: ColDef): ColDef => {
      if (colDef.field === SOURCE_COLUMN.name) return sourceColDef

      const column = columnFor(colDef.field)

      if (!column) return colDef

      const decorated: ColDef = {
        ...colDef,
        sortable: column.sortable,
        cellRenderer: (params: { data?: AssetRow }) => (
          <AssetCell row={params.data} column={column.name} kind={column.kind} />
        )
      }

      if (column.kind === AssetColumnKind.Number) {
        return { ...decorated, filter: 'agNumberColumnFilter', filterParams: NUMBER_FILTER_PARAMS }
      }

      /* Booleans keep the radio filter and timestamps the date range; only value lists change. */
      if (column.kind !== AssetColumnKind.String && column.kind !== AssetColumnKind.Array) return decorated

      return {
        ...decorated,
        filter: 'assetSetFilter',
        filterParams: { assetType, columnName: column.name }
      }
    },
    [assetType, columnFor, sourceColDef]
  )

  const {
    columns,
    setColumns,
    colDefs,
    setColDefs,
    isUpdatingColumns,
    buildAllColDefs,
    handleChangeColumns,
    restoreDefaultColumns
  } = useRecordsColumns({
    gridRef,
    columnValues,
    setColumnValues,
    getLookup,
    dataSourceOptions: NO_DATA_SOURCES,
    decorateColDef
  })

  /* Search only reaches the columns it is sent, so its scope follows the visible set. */
  const visibleColumns = useMemo<AssetColumn[]>(
    () =>
      (columns.length ? columns : gridColumns)
        .filter((column) => column.active)
        .map((column) => columnFor(column.name))
        .filter((column): column is AssetColumn => !!column),
    [columns, gridColumns, columnFor]
  )
  const pickerColumns = columns.length ? columns : gridColumns
  const isDefaultLayout =
    pickerColumns.length === gridColumns.length &&
    pickerColumns.every(
      (column, index) => column.name === gridColumns[index].name && column.active === gridColumns[index].active
    )

  /* The picker's order is the grid's order, so a drop moves the grid column with the list row. */
  const handleReorderColumns = useCallback(
    (name: string, target: string) => {
      const from = pickerColumns.findIndex((column) => column.name === name)
      const to = pickerColumns.findIndex((column) => column.name === target)

      if (from === -1 || to === -1) return

      const next = [...pickerColumns]
      next.splice(to, 0, ...next.splice(from, 1))
      setColumns(next)

      gridRef.current?.api.applyColumnState({
        state: next.filter((column) => column.active).map((column) => ({ colId: column.name })),
        applyOrder: true
      })
    },
    [pickerColumns, setColumns]
  )

  const visibleColumnsRef = useRef<AssetColumn[]>(visibleColumns)
  visibleColumnsRef.current = visibleColumns

  useEffect(() => {
    if (!gridColumns.length) return

    let cancelled = false

    buildAllColDefs(gridColumns).then((generated) => {
      if (!cancelled) setColDefs(generated)
    })

    return () => {
      cancelled = true
    }
    // Rebuilt only when the tab's column set changes; column toggles go through the hook.
  }, [gridColumns])

  /* Toggling a column only changes the request while a term is active, so only then refetch. */
  const searchScope = debouncedSearch ? visibleColumns.map((column) => column.name).join(',') : ''

  const datasource = useMemo(
    () => ({
      getRows: async (rowParams: any) => {
        const page = Math.max(1, Math.round(rowParams.request.endRow / PAGE_SIZE))

        /* The block past the cap is the notice row alone, served without a request. */
        if (page > MAX_PAGE) {
          rowParams.success({ rowData: [ROW_CAP], rowCount: MAX_ROWS + 1 })

          return
        }

        const filterModel = rowParams.api.getState()?.filter?.filterModel || {}
        const scope = visibleColumnsRef.current

        try {
          const response = await AssetInventoryServiceV4.getRecords({
            assetType,
            columns: assetColumns,
            lastSeen,
            page,
            limit: PAGE_SIZE,
            search: debouncedSearch || undefined,
            searchColumns: searchColumnsFor(scope),
            queryFilter: buildQueryFilter(filterModel, [...assetColumns, SOURCE_COLUMN], externalFilters),
            sort: buildSort(rowParams.request.sortModel || [], assetColumns)
          })

          setLoadFailed(false)
          setAvailable(response.available)
          setTotalResults(response.totalResults)
          setLastUpdatedAt(response.lastUpdatedAt)

          if (!response.totalResults) {
            rowParams.api.showNoRowsOverlay()
          } else {
            rowParams.api.hideOverlay()
          }

          const rowCount = response.totalResults > MAX_ROWS ? MAX_ROWS + 1 : response.totalResults

          rowParams.success({ rowData: response.rows, rowCount })
        } catch (error) {
          console.error(error)
          setLoadFailed(true)
          setTotalResults(0)
          setLastUpdatedAt(null)

          /* Succeeding with nothing keeps AG Grid off its "ERR" cell; the overlay carries the news. */
          rowParams.success({ rowData: [], rowCount: 0 })
          rowParams.api.showNoRowsOverlay()
        } finally {
          setIsFilterLoading(false)
          setInitialLoadDone(true)
        }
      }
    }),
    [assetType, lastSeen, assetColumns, externalFilters, debouncedSearch, searchScope]
  )

  /* Column filters were picked against the previous window's rows, so a new window starts unfiltered. */
  const previousLastSeen = useRef(lastSeen)

  useEffect(() => {
    if (previousLastSeen.current === lastSeen) return

    previousLastSeen.current = lastSeen

    const api = gridRef.current?.api

    if (api && Object.keys(api.getFilterModel() || {}).length) api.setFilterModel(null)
  }, [lastSeen])

  /**
   * getRows closes over the window, the shortcut filters and the search term, so a stale
   * datasource would keep serving the previous query. Re-registering it both installs the new
   * closure and makes the grid refetch from the first block.
   */
  useEffect(() => {
    if (!gridApi || columnsStatus === 'loading') return

    /* Rows are mapped through the columns, so without them there is nothing to ask for. */
    if (columnsStatus === 'failed') {
      setLoadFailed(true)
      setInitialLoadDone(true)
      gridApi.showNoRowsOverlay()

      return
    }

    gridApi.hideOverlay()
    setIsFilterLoading(true)
    gridApi.setGridOption('serverSideDatasource', datasource)
  }, [gridApi, datasource, columnsStatus])

  const lastScrollTop = useRef(0)
  const handleBodyScroll = useCallback(
    (event: BodyScrollEvent) => {
      if (event.direction !== 'vertical') return

      if (event.top > lastScrollTop.current) onScrollDown?.()

      lastScrollTop.current = event.top
    },
    [onScrollDown]
  )

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      const model = event.api.getState()?.filter?.filterModel || {}

      setActiveFilters(
        Object.entries(model)
          .map(([key, value]: [string, any]) => {
            const colDef = colDefs.find((col) => col.field === key || col.colId === key)

            const filterName = colDef?.headerName || key

            /* A Select All chip has to name the term, not the values it happened to match. */
            if (value?.filterType === ASSET_SET_FILTER) {
              const text = describeAssetSetFilter(value, key === SOURCE_COLUMN.name ? productTitle : undefined)

              return { filterName, filterValue: text ? [text] : [] }
            }

            const describe =
              value?.filterType === 'number'
                ? describeNumberFilter
                : value?.filterType === 'date'
                ? describeDateFilter
                : value?.filterType === 'boolean'
                ? describeBooleanFilter
                : null

            if (describe) {
              const text = describe(value)

              return { filterName, filterValue: text ? [text] : [] }
            }

            return { filterName, filterValue: value?.values || [] }
          })
          .filter((chip) => chip.filterValue.length > 0)
      )

      /* The stale "No Results" overlay would otherwise show through the loading rows until the refetch lands. */
      event.api.hideOverlay()
      setIsFilterLoading(true)
    },
    [colDefs]
  )

  const handleResetFilter = useCallback(
    (colName: string) => {
      if (!gridRef.current?.api) return

      if (colName === 'all') {
        gridRef.current.api.setFilterModel(null)
        setActiveFilters([])
        externalFilters.forEach((filter) => onClearFilter(filter.id))

        return
      }

      const shortcut = externalFilters.find((filter) => filter.label === colName)

      if (shortcut) {
        onClearFilter(shortcut.id)

        return
      }

      const colDef = colDefs.find((col) => col.headerName === colName || col.field === colName)
      const key = (colDef?.colId || colDef?.field) as string | undefined

      if (!key) return

      const model = { ...(gridRef.current.api.getFilterModel() || {}) }
      delete model[key]
      gridRef.current.api.setFilterModel(model)
      setActiveFilters((current) => current.filter((chip) => chip.filterName !== colName))
    },
    [colDefs, externalFilters, onClearFilter]
  )

  const shortcutChips = externalFilters.map((filter) => ({
    filterName: filter.label,
    filterValue: [filter.value]
  }))

  const components = useMemo(
    () => ({ agDateInput: DTPicker, booleanFilter: BooleanFilter, assetSetFilter: AssetSetFilter }),
    []
  )
  const defaultColDef = useMemo<ColDef>(() => ({ flex: 1, minWidth: 100, suppressHeaderMenuButton: true }), [])
  const loadingCellRendererParams = useMemo(() => ({ loadingMessage: 'One moment please...' }), [])
  const rowCapParams = useMemo(() => ({ total: totalResults, noun: title.toLowerCase() }), [totalResults, title])
  /* `available: false` means the account has no inventory table yet — not a filter that missed. */
  const unfiltered = !available || (!activeFilters.length && !externalFilters.length && !debouncedSearch)

  return (
    <AssetsGridWrapper>
      <Box className="grid-header">
        <Box className="grid-heading">
          <Box className="grid-icon">{icon}</Box>
          <Typography className="grid-title">{title}</Typography>
        </Box>

        <FilterChipContainer
          activeFilters={[...shortcutChips, ...activeFilters]}
          handleResetFilter={handleResetFilter}
        />

        <ClickAwayListener
          onClickAway={() => {
            setColumnsPopperOpen(false)
            setPopperAnchorEl(null)
          }}
        >
          <Box className="controls-container">
            {SHOW_ASSET_SEARCH && (
              <SearchInput search={search} setSearch={setSearch} placeholder={`Search ${title.toLowerCase()}`} />
            )}
            <LibraryDetailsHoverTooltip title="Manage Columns" placement="bottom-start">
              <div>
                <ControlButton
                  type="columns"
                  setAnchorEl={setPopperAnchorEl}
                  handleClick={() => setColumnsPopperOpen((open) => !open)}
                  open={columnsPopperOpen}
                />
              </div>
            </LibraryDetailsHoverTooltip>
            <AssetColumnPicker
              onReorder={handleReorderColumns}
              restoreDisabled={isDefaultLayout}
              anchorEl={popperAnchorEl}
              open={columnsPopperOpen}
              columns={columns}
              handleChangeColumns={handleChangeColumns}
              handleRestoreDefaultColumns={() => {
                setColumns(gridColumns)
                restoreDefaultColumns(gridColumns)
              }}
              isUpdating={isUpdatingColumns}
            />
          </Box>
        </ClickAwayListener>
      </Box>

      <Box className="grid-meta">
        <Typography className="meta-text">
          Number of rows:{' '}
          {!initialLoadDone || isFilterLoading ? (
            <HeaderSkeleton />
          ) : (
            <span className="meta-value">{totalResults.toLocaleString()}</span>
          )}
        </Typography>
        <Typography className="meta-text">
          Data as of:{' '}
          {!initialLoadDone || isFilterLoading ? (
            <HeaderSkeleton />
          ) : (
            <span className="meta-value">{formatDataAsOf(lastUpdatedAt)}</span>
          )}
        </Typography>
      </Box>

      <AssetsGridBody>
        {isFilterLoading && <FilterLoadingOverlay />}
        <AgGridReact<AssetRow>
          ref={gridRef}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          components={components}
          rowModelType="serverSide"
          getRowId={(params) => params.data.id}
          rowHeight={64}
          rowStyle={{ maxHeight: '60px' }}
          headerHeight={49}
          loading={false}
          loadingCellRenderer={LoadingRow}
          loadingCellRendererParams={loadingCellRendererParams}
          suppressRowVirtualisation={false}
          suppressServerSideFullWidthLoadingRow
          isFullWidthRow={(params) => params.rowNode.data?.id === ROW_CAP.id}
          fullWidthCellRenderer={RowCap}
          fullWidthCellRendererParams={rowCapParams}
          cacheBlockSize={PAGE_SIZE}
          maxBlocksInCache={5}
          serverSideInitialRowCount={PAGE_SIZE}
          rowBuffer={10}
          maxConcurrentDatasourceRequests={1}
          blockLoadDebounceMillis={200}
          animateRows={false}
          suppressScrollOnNewData
          suppressContextMenu
          tooltipShowDelay={200}
          suppressNoRowsOverlay={false}
          noRowsOverlayComponent={() =>
            loadFailed ? (
              <ErrorOverlay />
            ) : (
              <NoResultsFound blank={unfiltered} blankDescription={`No ${title.toLowerCase()} for this account yet`} />
            )
          }
          postProcessPopup={positionRecordsGridPopup}
          onGridReady={(event: GridReadyEvent) => setGridApi(event.api)}
          onFilterChanged={handleFilterChanged}
          onRowClicked={(event) => event.data && event.data.id !== ROW_CAP.id && onRowClick?.(event.data)}
          onBodyScroll={handleBodyScroll}
        />
      </AssetsGridBody>
    </AssetsGridWrapper>
  )
}

export default AssetsGrid
